import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, Gauge, Fuel, Settings2, Calendar, Users, Car, Palette, BadgeCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { API, formatINR, imgUrl } from "../lib/api";
import { SellerBadge } from "../components/CarCard";
import ContactActions from "../components/ContactActions";
import EmiCalculator from "../components/EmiCalculator";
import DealerLogo from "../components/DealerLogo";
import FavoriteButton from "../components/FavoriteButton";
import ShareButton, { carShare } from "../components/ShareButton";
import { Share2 } from "lucide-react";
import { cityPath } from "./Cities";
import BackButton from "../components/BackButton";

const Spec = ({ icon: Icon, label, value }) => (
  <div className="p-4 rounded-2xl bg-black/40 border border-white/5">
    <Icon className="w-4 h-4 text-[#D4AF37] mb-2" />
    <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
    <p className="text-sm font-semibold text-white mt-0.5">{value || "—"}</p>
  </div>
);

export default function CarDetail() {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [active, setActive] = useState(0);
  const [error, setError] = useState(false);
  useEffect(() => {
    axios.get(`${API}/listings/${id}`).then((r) => setCar(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) return <div className="pt-40 text-center text-gray-400" data-testid="car-not-found">This listing is no longer available. <Link to="/cars" className="text-[#E5C158]">Browse cars</Link></div>;
  if (!car) return <div className="pt-40 text-center text-gray-500">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28" data-testid="car-detail-page">
      <BackButton className="mb-6" />
      <div className="grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-6">
          <div className="relative rounded-3xl overflow-hidden border gold-line aspect-[16/10] bg-black">
            <img src={imgUrl(car.images[active])} alt="" className="w-full h-full object-cover" data-testid="gallery-main-image" />
            {car.images.length > 1 && <>
              <button onClick={() => setActive((active - 1 + car.images.length) % car.images.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 border border-white/10 hover:border-[#D4AF37] flex items-center justify-center" aria-label="Previous photo" data-testid="gallery-prev"><ChevronLeft className="w-5 h-5" /></button>
              <button onClick={() => setActive((active + 1) % car.images.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 border border-white/10 hover:border-[#D4AF37] flex items-center justify-center" aria-label="Next photo" data-testid="gallery-next"><ChevronRight className="w-5 h-5" /></button>
              <span className="absolute bottom-4 left-4 text-xs bg-black/60 px-2 py-1 rounded-full" data-testid="gallery-counter">{active + 1} / {car.images.length}</span>
            </>}
            <div className="absolute top-4 left-4 flex gap-2"><SellerBadge type={car.seller_type} />{car.is_demo && <span className="text-[10px] uppercase bg-black/70 px-2 py-1 rounded-full text-gray-300">Demo listing</span>}</div>
            <FavoriteButton id={car.id} className="absolute bottom-4 right-4 w-12 h-12" size="w-5 h-5" />
            <ShareButton {...carShare(car)} testid="car-detail-share-btn" className="absolute bottom-4 right-[4.5rem] h-12 px-4 rounded-full bg-black/60 backdrop-blur border border-white/10 hover:border-[#D4AF37] flex items-center gap-2 text-sm text-white"><Share2 className="w-4 h-4" />Share</ShareButton>
            {car.status === "sold" && <span className="absolute top-4 right-4 bg-red-600 font-bold px-3 py-1 rounded">SOLD</span>}
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {car.images.map((u, i) => (
              <button key={u} onClick={() => setActive(i)} className={`shrink-0 w-28 aspect-[4/3] rounded-xl overflow-hidden border-2 transition-colors ${i === active ? "border-[#D4AF37]" : "border-transparent opacity-60 hover:opacity-100"}`} data-testid={`gallery-thumb-${i}`}>
                <img src={imgUrl(u)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div>
            <p className="eyebrow mb-2">{car.body_type}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl" data-testid="car-detail-title">{car.year} {car.brand} {car.model}</h1>
            <p className="text-gray-400 mt-1">{car.variant}</p>
            <Link to={cityPath(car.state, car.city)} className="text-sm text-gray-400 hover:text-[#E5C158] flex items-center gap-1 mt-3" data-testid="car-detail-city-link"><MapPin className="w-4 h-4 text-[#D4AF37]" />{car.city}, {car.state}</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="car-specs">
            <Spec icon={Calendar} label="Year" value={car.year} />
            <Spec icon={Gauge} label="KM Driven" value={`${car.km_driven.toLocaleString("en-IN")} km`} />
            <Spec icon={Fuel} label="Fuel" value={car.fuel} />
            <Spec icon={Settings2} label="Transmission" value={car.transmission} />
            <Spec icon={Users} label="Owners" value={`${car.owners}${["st", "nd", "rd"][car.owners - 1] || "th"} owner`} />
            <Spec icon={Car} label="Body" value={car.body_type} />
            <Spec icon={Palette} label="Colour" value={car.color} />
            <Spec icon={MapPin} label="Registered" value={car.state} />
          </div>
          {car.description && (
            <div className="lux-card p-6"><p className="eyebrow mb-3">About this car</p><p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">{car.description}</p></div>
          )}
        </div>
        <aside className="space-y-6 lg:sticky lg:top-24 self-start">
          <div className="lux-card p-6 space-y-5">
            <div><p className="text-xs uppercase tracking-widest text-gray-500">Asking price</p><p className="font-display text-5xl gold-text" data-testid="car-detail-price">{formatINR(car.price)}</p>
              {car.previous_price > car.price && <p className="text-xs mt-1" data-testid="car-detail-price-drop"><span className="line-through text-gray-500">{formatINR(car.previous_price)}</span> <span className="text-emerald-400 font-semibold">Price dropped by {formatINR(car.previous_price - car.price)}</span></p>}
            </div>
            <ContactActions car={car} />
          </div>
          <div className="lux-card p-6" data-testid="seller-card">
            <p className="eyebrow mb-4">Seller</p>
            {car.dealer ? (
              <Link to={`/dealers/${car.dealer.id}`} className="flex items-center gap-4 group" data-testid="seller-dealer-link">
                <DealerLogo dealer={car.dealer} size="w-14 h-14" text="text-xl" />
                <div>
                  <p className="font-semibold flex items-center gap-1 group-hover:text-[#E5C158]">{car.dealer.name}{car.dealer.verified && <BadgeCheck className="w-4 h-4 text-[#D4AF37]" />}</p>
                  <p className="text-xs text-gray-400">{car.dealer.address}, {car.dealer.city}</p>
                  <p className="text-xs text-[#E5C158] mt-1">Visit store →</p>
                </div>
              </Link>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full border border-[#D4AF37]/40 flex items-center justify-center font-display text-xl gold-text">{car.seller_name?.[0]}</div>
                <div><p className="font-semibold">{car.seller_name}</p><p className="text-xs text-gray-400">Private owner · {car.city}</p></div>
              </div>
            )}
          </div>
          <EmiCalculator price={car.price} />
        </aside>
      </div>
    </div>
  );
}
